import React from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Divider,
  Chip,
  Box,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Checkbox,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  CloudDownload,
  DownloadOutlined,
  // VideocamOutlined,
  // CloseOutlined,
  Grid4x4Outlined,
  LiveTv,
  PlayArrow,
  //PlayArrowOutlined,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";

import { useSnackbarConsumer } from "shared/providers/Snackbar";
import { useIntl, defineMessages } from "react-intl";
import { FileFilters } from "modules/files/components/FileFilters";
import {
  createLog,
  downloadFile,
  getPresignedURLByFileId,
} from "modules/files/services";
import { formatDate } from "shared/parsers/date";
import { useFileFilters } from "../../providers/FileFilters";
import { IFile } from "modules/files/types";
import { Player } from "../../components/Player";
import { BiResume } from "../../components/BiResume";
// import { storeBatchDownload } from "../../services";
import { humanFileSize } from "shared/parsers/filesize";
import { VideoGrid } from "../VideoGrid";
import { VideoLiveGrid } from "../VideoLiveGrid";
import { Badge } from "rsuite";
import { useAuthenticationConsumer } from "shared/providers/Authentication";
import { toMP4 } from "../QueuedDownloads/services/mp4";

const messages = defineMessages({
  downloadError: { id: "files.download.error" },
  downloadSuccess: { id: "files.download.success" },
  noSelected: { id: "files.grid.error" },
  scheduledDownload: { id: "files.scheduled.downloads" },
  reachDailyLimit: { id: "files.scheduled.reach.daily.limit" },
  myVideos: { id: "files.my.videos" },
  myFiles: { id: "files.my.files" },
  live: { id: "live" },
  downloadSelected: { id: "download.selected" },
  openInGrid: { id: "files.open.in.grid" },
  show: { id: "show" },
  perPage: { id: "perPage" },
  searchingYourFiles: { id: "files.searching.your.files" },
  company: { id: "files.company" },
  name: { id: "files.name" },
  camera: { id: "files.camera" },
  recordedAt: { id: "files.recordedAt" },
  type: { id: "files.type" },
  size: { id: "files.size" },
  actions: { id: "files.actions" },
  undefined: { id: "undefined" },
  downloadFile: { id: "files.download.file" },
  scheduleFile: { id: "files.schedule.file" },
  noFilesFound: { id: "files.no.files.found" },
  weAreLoadingVideoForYou: { id: "files.we.are.loading.video.for.you" },
  requestDownloadTitle: { id: "files.request.download.title" },
  requestDownloadText1: { id: "files.request.download.text1" },
  requestDownloadText2: { id: "files.request.download.text2" },
  close: { id: "close" },
  loading: { id: "loading" },
  selectFilesAlert: { id: "files.request.select.alert" },
  maxGridError: { id: "files.grid.max.error" },
});

const FilesDashboard = () => {
  const location = useLocation();
  const isVideoList: boolean = location.pathname === "/videos";
  const {
    files,
    perPage,
    loading,
    setLoading,
    handleChangePage,
    handleChangePerPage,
    meta,
  } = useFileFilters();

  const { openSnackbar } = useSnackbarConsumer();
  const { formatMessage } = useIntl();
  const { readOnly } = useAuthenticationConsumer();

  const [checkedAll, setCheckedAll] = React.useState<boolean>(false);
  const [videoOpen, setVideoOpen] = React.useState<boolean>(false);
  const [videoName, setVideoName] = React.useState<string>("");
  const [videoURL, setVideoURL] = React.useState<any>("");
  const [loadingFull] = React.useState<boolean>(false);
  const [loadingBatch, setLoadingBatch] = React.useState<boolean>(false);
  const [selectedItems, setSelectedItems] = React.useState<number[]>([]);
  const [selectedItemsNames, setSelectedItemsNames] = React.useState<string[]>(
    []
  );
  const [openBatchDownload, setOpenBatchDownload] =
    React.useState<boolean>(false);
  const [gridLoading, setGridLoading] = React.useState<boolean>(true);
  const [videosGrid, setVideosGrid] = React.useState<string[]>([]);
  const [videosGridOpen, setVideosGridOpen] = React.useState<boolean>(false);
  const [loadingVideo, setLoadingVideo] = React.useState<boolean>(false);

  const [liveOpen, setLiveOpen] = React.useState<boolean>(false);

  const handleDownload = async (video: IFile, force: boolean = false) => {
    setLoading(true);

    const { status, data: dataDownload } = await downloadFile(video.id);

    if (status === 200) {
      if (dataDownload.url === "queued") {
        openSnackbar({
          message: formatMessage(messages.scheduledDownload),
          severity: "success",
        });
      } else {
        window.open(dataDownload.url);
        openSnackbar({
          message: formatMessage(messages.downloadSuccess),
          severity: "success",
        });
      }
    } else if (status === 409) {
      openSnackbar({
        message: formatMessage(messages.reachDailyLimit),
        severity: "warning",
      });
    } else {
      openSnackbar({
        message: formatMessage(messages.downloadError),
        severity: "error",
      });
    }
    setLoading(false);
  };

  // const handleWatch = async (id: number) => {
  //   setLoading(true);
  //   const { status, data: dataWatch } = await watchFile(id);
  //   if (status === 200) {
  //     return dataWatch;
  //   } else {
  //     openSnackbar({
  //       message: formatMessage(messages.downloadError),
  //       severity: "error",
  //     });
  //   }
  //   setLoading(false);
  // };

  const handleVideoOpen = async (
    url: string,
    fileName: string,
    download = false,
    id?: number
  ) => {
    setLoadingVideo(true);

    if (download) {
      const { data: dataDownload } = await getPresignedURLByFileId(id);

      openSnackbar({
        message: "Iniciando download...",
        severity: "info",
      });
      setTimeout(() => {
        window.open(decodeURIComponent(dataDownload.url));
      }, 1000);
      setLoadingVideo(false);
      return false;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    if (fileName && url) {
      if (url.toLowerCase().includes(".mp4")) {
        const { data: dataMP4 } = await getPresignedURLByFileId(id);
        setVideoURL(decodeURIComponent(dataMP4.url));
        setVideoName(fileName);
        setVideoOpen(true);
        setLoadingVideo(false);
        return false;
      }

      setLoadingVideo(true);

      const { status, data } = await toMP4([url]);

      if (status === 201 && data.bufferUrls[0]) {
        await createLog({ file_id: id, log_type: "REQUEST_WATCH_VIDEO" });
        setVideoURL(data.bufferUrls[0]);
        setVideoName(fileName);
        setVideoOpen(true);
      } else {
        setVideoURL("");
        setVideoName("");
        setVideoOpen(false);
        setLoadingVideo(false);
        openSnackbar({
          message: "Não foi possível realizar a operação.",
          severity: "warning",
        });
      }
    }
    setLoadingVideo(false);
  };

  const resetVideoOpen = () => {
    setVideoURL("");
    setVideoName("");
    setVideoOpen(false);
    setLoading(false);
  };

  const handleChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    let selecteds: number[] = selectedItems;
    let names: string[] = selectedItemsNames;
    const value = parseInt(event.target.value);
    const name = event.target.name;

    if (event.target.checked) {
      selecteds.push(value);
      names.push(name);
    } else {
      const key = Object.keys(selecteds).find(
        (key) => selecteds[key] === value
      );
      delete selecteds[key];

      const keyName = Object.keys(names).find((keyn) => names[keyn] === name);
      delete names[keyName];
    }

    selecteds = selecteds.filter((item) => item !== undefined && item !== 0);
    names = names.filter((item) => item !== undefined && item !== "");

    setSelectedItems([...selecteds]);
    setSelectedItemsNames([...names]);
  };

  const handleAllChecked = React.useCallback(() => {
    if (checkedAll) {
      let selecteds: number[] = [];
      let names: string[] = [];
      files.forEach((file: IFile) => {
        selecteds.push(file.id);
        names.push(file.name);
      });
      setSelectedItems([...selecteds]);
      setSelectedItemsNames([...names]);
    } else {
      setSelectedItems([]);
      setSelectedItemsNames([]);
    }
  }, [files, checkedAll]);

  // const handleBatchDownload = async () => {
  //   setLoadingBatch(true);

  //   if (!selectedItems.length) {
  //   }

  //   const { status } = await storeBatchDownload(selectedItems);
  //   if (status === 200) {
  //     window.location.href = "/downloads";
  //   } else {
  //     setLoadingBatch(false);
  //   }
  // };

  const handleDownloadOriginalFiles = async () => {
    setLoadingBatch(true);
    for (let videoId of selectedItems) {
      const x = await downloadFile(videoId);
      const { status, data } = x;

      if (status === 409) {
        openSnackbar({
          message: formatMessage(messages.reachDailyLimit),
          severity: "warning",
        });
        setLoadingBatch(false);
      } else if (data && data.url === "queued") {
        openSnackbar({
          message: formatMessage(messages.downloadSuccess),
          severity: "success",
        });
      } else {
        openSnackbar({
          message: formatMessage(messages.downloadSuccess),
          severity: "success",
        });
        await new Promise((r) => setTimeout(r, 1000));
      }
    }
    setOpenBatchDownload(false);
    setLoadingBatch(false);
  };

  // const handleBatchCancel = () => {
  //   setSelectedItems([]);
  //   setSelectedItemsNames([]);
  //   setOpenBatchDownload(false);
  // };

  const handleVideoGrid = async () => {
    if (!selectedItems.length) {
      openSnackbar({
        message: formatMessage(messages.noSelected),
        severity: "error",
      });
      return false;
    }

    if (selectedItems.length > 4) {
      openSnackbar({
        message: formatMessage(messages.maxGridError),
        severity: "warning",
      });
      return false;
    }

    setGridLoading(true);
    setVideosGridOpen(true);

    const urls: string[] = [];

    for (let videoId of selectedItems) {
      const { data } = await getPresignedURLByFileId(videoId);

      if (data && data.url) {
        // const originalURL = decodeURIComponent(data.url);
        const url = decodeURIComponent(
          data.url.replace(/(.*\.(h264|h265|dav)).*/, "$1")
        );

        urls.push(url);
      }
    }

    const { data: dataURLs } = await toMP4(urls);

    setVideosGrid(dataURLs.bufferUrls);
    setGridLoading(false);
  };

  React.useEffect(() => {
    handleAllChecked();
  }, [handleAllChecked]);

  return (
    <Container maxWidth={false}>
      <VideoLiveGrid
        open={liveOpen}
        onClose={() => setLiveOpen(false)}
        data={meta.live}
      />
      <BiResume data={meta.totalDashboard} loading={loading} />
      <Grid container alignItems="center" justifyContent="space-between">
        <Typography variant="h5" component="h4" fontWeight="bold">
          {isVideoList
            ? formatMessage(messages.myVideos)
            : formatMessage(messages.myFiles)}
        </Typography>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "flex-end",
            marginTop: "15px",
          }}
        >
          <FileFilters isVideoList={isVideoList} isFileScreen={true} />
        </Box>
      </Grid>

      <Box sx={{ display: "flex", width: "100%" }}>
        {loading && (
          <Box sx={{ mt: 3, width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </Box>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 3, mb: 3 }}
      >
        <div>
          {meta && meta.live && meta.live.length > 0 && (
            <>
              <Button
                type="button"
                variant="contained"
                size="small"
                startIcon={<LiveTv />}
                onClick={() => setLiveOpen(true)}
                color="success"
              >
                {formatMessage(messages.live)}
              </Button>
              &nbsp;&nbsp;
            </>
          )}
          {!readOnly && (
            <Button
              type="button"
              variant="contained"
              size="small"
              startIcon={<DownloadOutlined />}
              onClick={() => {
                if (isVideoList) {
                  setOpenBatchDownload(!openBatchDownload);
                } else {
                  handleDownloadOriginalFiles();
                }
              }}
            >
              {formatMessage(messages.downloadSelected)}
            </Button>
          )}
          &nbsp;&nbsp;
          {/* <Button
            type="button"
            variant="contained"
            size="small"
            startIcon={<Grid4x4Outlined />}
            onClick={() => handleVideoGrid()}
          >
            {formatMessage(messages.openInGrid)}
          </Button> */}
        </div>
        <Box>
          {formatMessage(messages.show)}{" "}
          <Select
            placeholder={formatMessage(messages.show)}
            size="small"
            value={perPage}
            onChange={(e) => handleChangePerPage(e.target.value)}
          >
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={150}>150</MenuItem>
            <MenuItem value={200}>200</MenuItem>
            <MenuItem value={300}>300</MenuItem>
            <MenuItem value={300}>300</MenuItem>
            <MenuItem value={500}>500</MenuItem>
          </Select>{" "}
          {formatMessage(messages.perPage)}
        </Box>
      </Grid>
      <Paper
        sx={{
          width: "100%",
          borderRadius: 4,
          boxShadow:
            "rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px",
        }}
      >
        {loading ? (
          <p
            style={{
              padding: "15px",
              width: "100%",
              textAlign: "center",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            {formatMessage(messages.searchingYourFiles)}
          </p>
        ) : (
          <>
            <TableContainer>
              <Table aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell width={10}>
                      <Checkbox
                        onChange={() => setCheckedAll(!checkedAll)}
                        name="checkall"
                        checked={checkedAll}
                      />
                    </TableCell>
                    <TableCell>{formatMessage(messages.company)}</TableCell>
                    <TableCell>{formatMessage(messages.name)}</TableCell>
                    {isVideoList && (
                      <>
                        <TableCell>{formatMessage(messages.camera)}</TableCell>
                        <TableCell>
                          {formatMessage(messages.recordedAt)}
                        </TableCell>
                      </>
                    )}
                    <TableCell>{formatMessage(messages.type)}</TableCell>
                    <TableCell>{formatMessage(messages.size)}</TableCell>
                    <TableCell>{formatMessage(messages.actions)}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.length > 0 ? (
                    files.map((item: IFile) => (
                      <TableRow
                        key={item.id}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                      >
                        <TableCell>
                          <Checkbox
                            onChange={handleChecked}
                            value={item.id}
                            name={item.name}
                            checked={selectedItems.includes(item.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {item.company.fantasy_name ||
                            formatMessage(messages.undefined)}
                        </TableCell>
                        <TableCell>{item.name}</TableCell>
                        {isVideoList && (
                          <>
                            <TableCell>
                              {item.camera?.name ||
                                formatMessage(messages.undefined)}
                            </TableCell>
                            <TableCell>
                              {item.recording_dvr_date
                                ? formatDate(
                                    item.recording_dvr_date,
                                    "dd/MM/yyyy HH:mm:ss"
                                  )
                                : formatMessage(messages.undefined)}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          <Chip
                            label={
                              item.fileType?.name ||
                              formatMessage(messages.undefined)
                            }
                          />
                        </TableCell>
                        <TableCell>{humanFileSize(item.size || 0)}</TableCell>
                        <TableCell>
                          {isVideoList && item && item.fileMetaData && (
                            <IconButton
                              disabled={
                                ![1, 16, 19, 4, 17].includes(
                                  item.file_type_id
                                ) ||
                                item.fileMetaData.s3_data.StorageClass !==
                                  "STANDARD"
                              }
                              color="primary"
                              size="small"
                              aria-label="Visualizar"
                              onClick={() =>
                                handleVideoOpen(
                                  `https://${item.company.bucket}.s3.us-east-1.amazonaws.com/${item.partner_name}`,
                                  item.name,
                                  false,
                                  item.id
                                )
                              }
                            >
                              <Tooltip title={"Assistir"}>
                                <PlayArrow />
                              </Tooltip>
                            </IconButton>
                          )}
                          {!readOnly && (
                            <IconButton
                              color="primary"
                              size="small"
                              aria-label={formatMessage(messages.downloadFile)}
                              onClick={() => {
                                if (item.id) handleDownload(item);
                              }}
                            >
                              <Tooltip
                                title={
                                  item.storage.uid === "S3"
                                    ? formatMessage(messages.scheduleFile)
                                    : formatMessage(messages.downloadFile)
                                }
                              >
                                {item.storage.uid === "S3" ? (
                                  <Badge content="AD" color="orange">
                                    <CloudDownload />
                                  </Badge>
                                ) : (
                                  <CloudDownload />
                                )}
                              </Tooltip>
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7}>
                        {formatMessage(messages.noFilesFound)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />
            <TablePagination
              component="div"
              rowsPerPageOptions={[meta.per_page]}
              count={meta.total || 0}
              rowsPerPage={meta.per_page || 0}
              page={meta.current_page - 1 || 0}
              onPageChange={handleChangePage}
            />
          </>
        )}
      </Paper>

      <Player
        open={videoOpen}
        videoName={videoName}
        videoURL={videoURL}
        onClose={() => resetVideoOpen()}
      />
      <Dialog
        open={loadingFull}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" style={{ textAlign: "center" }}>
          {formatMessage(messages.weAreLoadingVideoForYou)}
        </DialogTitle>
      </Dialog>

      {/* <Dialog
        open={openBatchDownload}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" style={{ textAlign: "center" }}>
          SOLICITAÇÃO DE DOWNLOAD
          <hr />
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{ textAlign: "center" }}
          >
            Os arquivos de segurança precisam ser convertidos em um formato que
            possibilite serem assistidos em seu computador. Essa conversão leva
            algum tempo para ser concluída em função do tamanho dos arquivos,
            mas você pode acompanhar a evolução na sua área de{" "}
            <a href="/downloads" target="_blank">
              Downloads
            </a>
            . Porém, se preferir, também é possível baixar no formato original,
            mas é necessário utilizar um{" "}
            <a
              href="https://dw.uptodown.com/dwn/KJfcVpQNSfCfO7xGDBbQwyQrTi45pr_miF1X8q7fDo3WDn_OmGtZqhlkjs-j7VbmCPBax2AabZ4C8FcpWwwicWHs_YCU5jWQnXaXt3yQlV6H0U-z4EWm0f-6S4Il5ihh/1La0OyNfdVtsKeZRdO7yIL9dCNtUIzMZPFuBiAW0k7yi40QLno7YsekQLMbC00N8sA7gAJqVSQWZJYZIexbszjAvMX6nQ9J_LP6du9CSjFUDqccc7SynoTzXaSbJjRQa/lscZhYKjMLMYD3wJQXY4FRdBnPc4JLFTVaa2N_akyBj_GzE8qMdhUbxKV_8SQd6SnC60akuLAzg1BF-k-IpmvHDlvxxxOls6eJDY5oGMuB4=/"
              target="_blank"
              rel="noreferrer"
            >
              player específico
            </a>{" "}
            para reprodução.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {selectedItems.length > 0 ? (
            <div
              style={{
                width: "80%",
                display: "flex",
                flexDirection: "column",
                margin: "0 auto 20px auto",
              }}
            >
              {!loadingBatch ? (
                <>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<DownloadOutlined />}
                    onClick={() => handleDownloadOriginalFiles()}
                  >
                    Baixar Original
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<VideocamOutlined />}
                    onClick={() => handleBatchDownload()}
                    style={{ marginTop: "24px" }}
                  >
                    Solicitar Conversão para MP4
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<CloseOutlined />}
                    onClick={() => handleBatchCancel()}
                    style={{ marginTop: "24px" }}
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <p
                  style={{
                    width: "100%",
                    textAlign: "center",
                    display: "inline-flex",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={20} />
                  &nbsp;&nbsp; Aguarde...
                </p>
              )}
            </div> */}
      <Dialog
        open={openBatchDownload}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" style={{ textAlign: "center" }}>
          {formatMessage(messages.requestDownloadTitle)}
          <hr />
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{ textAlign: "center" }}
          >
            {formatMessage(messages.requestDownloadText1)}{" "}
            <a href="/downloads" target="_blank">
              Downloads
            </a>
            .&nbsp;{formatMessage(messages.requestDownloadText2)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {selectedItems.length > 0 ? (
            <div
              style={{
                width: "80%",
                display: "flex",
                flexDirection: "column",
                margin: "0 auto 20px auto",
              }}
            >
              {!loadingBatch ? (
                <>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<DownloadOutlined />}
                    onClick={() => handleDownloadOriginalFiles()}
                  >
                    Download
                  </Button>
                </>
              ) : (
                <p
                  style={{
                    width: "100%",
                    textAlign: "center",
                    display: "inline-flex",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={20} />
                  &nbsp;&nbsp; {formatMessage(messages.loading)}
                </p>
              )}
            </div>
          ) : (
            <h5
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
                color: "red",
              }}
            >
              {formatMessage(messages.selectFilesAlert)}
              <br />
              <br />
              <Button
                type="button"
                variant="contained"
                onClick={() => setOpenBatchDownload(false)}
              >
                {formatMessage(messages.close)}
              </Button>
            </h5>
          )}
        </DialogActions>
      </Dialog>
      <VideoGrid
        videos={videosGrid}
        open={videosGridOpen}
        loading={gridLoading}
        onClose={() => {
          setVideosGrid([]);
          setVideosGridOpen(false);
        }}
      />
      {loadingVideo && (
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFF",
            fontSize: "30px",
            fontWeight: "bold",
            gap: 30,
          }}
        >
          <div>
            <CircularProgress size={80} />
          </div>
          <div>Solicitando vídeo, aguarde...</div>
        </div>
      )}
    </Container>
  );
};

export { FilesDashboard };
